import React, { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Search, Plus, Save, AlertTriangle, ShieldCheck, ShieldAlert, Trash2, Edit2, X, LayoutDashboard, Users, Briefcase, MessageSquare, CreditCard, BookOpen, FolderOpen, Settings, ChevronRight, Lock } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import api from "../../api.js";
import { usePermissions } from "../../context/PermissionsContext";
import { useNotification } from "../../context/NotificationContext";
import { useModal } from "../../context/ModalContext";
import MiniLoader from "../../components/MiniLoader";

const IconMap = {
  LayoutDashboard, Users, Briefcase, MessageSquare, CreditCard, BookOpen, FolderOpen, Settings
};

const PERMISSIONS_SCHEMA = [
  {
    category: "Tableau de bord",
    masterCode: "DASHBOARD_ACCESS",
    icon: "LayoutDashboard",
    permissions: []
  },
  {
    category: "CRM",
    masterCode: "CRM_ACCESS",
    icon: "Users",
    permissions: [
      { code: "CRM_LEADS_MANAGE", label: "Gérer les leads" },
      { code: "CRM_PROSPECTS_MANAGE", label: "Gérer les prospects" },
      { code: "CRM_CLIENTS_MANAGE", label: "Gérer les clients" },
      { code: "CRM_COMPANIES_MANAGE", label: "Gérer les sociétés" },
    ]
  },
  {
    category: "Conseil",
    masterCode: "CONSEIL_ACCESS",
    icon: "Briefcase",
    permissions: [
      { code: "CONSEIL_AUDIT_MANAGE", label: "Gérer les audits" },
      { code: "CONSEIL_ALLOC_MANAGE", label: "Gérer les allocations" },
    ]
  },
  {
    category: "Forum",
    masterCode: "FORUM_ACCESS",
    icon: "MessageSquare",
    permissions: [
      { code: "FORUM_DOSSIER_MANAGE", label: "Gérer les dossiers" },
      { code: "FORUM_DOC_MANAGE", label: "Gérer les documents forum" },
    ]
  },
  {
    category: "Transactions",
    masterCode: "TRANS_ACCESS",
    icon: "CreditCard",
    permissions: [
      { code: "TRANS_MANAGE", label: "Gérer les transactions" },
    ]
  },
  {
    category: "Catalogue",
    masterCode: "CATALOG_ACCESS",
    icon: "BookOpen",
    permissions: [
      { code: "CATALOG_PART_MANAGE", label: "Gérer les partenaires" },
      { code: "CATALOG_PROD_MANAGE", label: "Gérer les produits" },
    ]
  },
  {
    category: "GED",
    masterCode: "GED_ACCESS",
    icon: "FolderOpen",
    permissions: [
      { code: "GED_MANAGE", label: "Gestion des fichiers" },
    ]
  },
  {
    category: "Tâches & Calendrier",
    masterCode: "TASKS_CAL_ACCESS",
    icon: "Briefcase",
    permissions: [
      { code: "TASKS_MANAGE", label: "Gestion des tâches" },
      { code: "CALENDAR_MANAGE", label: "Gestion du calendrier" },
    ]
  },
  {
    category: "Outils",
    masterCode: "TOOLS_ACCESS",
    icon: "LayoutDashboard",
    permissions: [
      { code: "TOOLS_QUANTALYS", label: "Accès Quantalys" },
      { code: "TOOLS_IMPOTS", label: "Accès simulateur Impôts" },
      { code: "TOOLS_BREVO", label: "Accès Brevo" },
      { code: "TOOLS_ALLOCATION", label: "Accès simulateur Allocation" },
    ]
  },
  {
    category: "Support",
    masterCode: "SUPPORT_ACCESS",
    icon: "Users",
    permissions: [
      { code: "SUPPORT_TICKETS_MANAGE", label: "Gestion des tickets" },
    ]
  },
  {
    category: "Administrateur",
    masterCode: "ADMIN_ACCESS",
    icon: "Settings",
    permissions: [
      { code: "SET_WORKSPACE_MANAGE", label: "Gérer le Workspace" },
      { code: "SET_MEMBERS_MANAGE", label: "Gérer les membres" },
      { code: "SET_ROLES_MANAGE", label: "Gérer les rôles", riskLevel: "CRITICAL" },
      { code: "SET_SECURITY_MANAGE", label: "Sécurité & Audit", riskLevel: "SENSITIVE" },
    ]
  }
];

const RolesManager = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(PERMISSIONS_SCHEMA[0].category);
  const [search, setSearch] = useState("");
  const { showToast } = useNotification();

  // Modal State for new Role
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleColor, setNewRoleColor] = useState("#6366f1");

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/roles`);
      setRoles(response.data);
      if (response.data.length > 0 && !selectedRoleId) {
        setSelectedRoleId(response.data[0].id);
      }
    } catch (error) {
      console.error("Erreur de chargement des rôles", error);
      showToast("Erreur lors du chargement des rôles", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(roles);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedRoles = items.map((r, index) => ({
      ...r,
      position: items.length - index
    }));

    setRoles(updatedRoles);

    try {
      await api.put(`/roles/${reorderedItem.id}/position`, { position: updatedRoles[result.destination.index].position });
    } catch (error) {
      console.error("Failed to update role position", error);
      showToast("Erreur lors de la sauvegarde de l'ordre", "error");
    }
  };

  const sortedRoles = [...roles].sort((a, b) => {
    const order = { 'SYSADMIN': 1, 'ADMIN': 2, 'MODERATOR': 3 };
    const aOrder = order[a.code] || 4;
    const bOrder = order[b.code] || 4;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return b.position - a.position;
  });

  const selectedRole = roles.find(r => r.id === selectedRoleId);

  const [passwordModal, setPasswordModal] = useState({ isOpen: false, roleId: null, permissionCode: null, password: "", error: "", isLoading: false });

  const executeToggle = async (roleId, permissionCode) => {
    let newRoles = [...roles];
    const roleIndex = newRoles.findIndex(r => r.id === roleId);
    if (roleIndex === -1) return;
    
    const role = newRoles[roleIndex];
    const hasPerm = role.rolePermissions.some(rp => rp.permission.code === permissionCode);
    let newPerms = [...role.rolePermissions];
    
    const section = PERMISSIONS_SCHEMA.find(s => 
      s.masterCode === permissionCode || s.permissions.some(p => p.code === permissionCode)
    );

    if (hasPerm) {
      newPerms = newPerms.filter(rp => rp.permission.code !== permissionCode);
      if (section && section.masterCode === permissionCode) {
        const subCodes = section.permissions.map(p => p.code);
        newPerms = newPerms.filter(rp => !subCodes.includes(rp.permission.code));
      }
    } else {
      newPerms.push({ permission: { code: permissionCode } });
      if (section && section.masterCode !== permissionCode) {
        const hasMaster = newPerms.some(rp => rp.permission.code === section.masterCode);
        if (!hasMaster) {
          newPerms.push({ permission: { code: section.masterCode } });
        }
      }
    }
    
    newRoles[roleIndex] = { ...role, rolePermissions: newPerms };
    setRoles(newRoles);

    const permCodes = newPerms.map(rp => rp.permission.code);
    try {
      await api.put(`/roles/${roleId}/permissions`, { permissions: permCodes });
    } catch (err) {
      console.error(err);
      showToast("Erreur lors de la sauvegarde des permissions", "error");
    }
  };

  const togglePermission = async (roleId, permissionCode, riskLevel) => {
    // Si c'est dans la catégorie Administrateur (ou que c'est le master Administrateur), demander le mot de passe
    const section = PERMISSIONS_SCHEMA.find(s => 
      s.masterCode === permissionCode || s.permissions.some(p => p.code === permissionCode)
    );
    
    if (section && section.category === "Administrateur") {
      setPasswordModal({ isOpen: true, roleId, permissionCode, password: "", error: "", isLoading: false });
      return;
    }

    await executeToggle(roleId, permissionCode);
  };

  const handlePasswordConfirm = async (e) => {
    e.preventDefault();
    setPasswordModal(prev => ({ ...prev, isLoading: true, error: "" }));
    
    try {
      const response = await api.post('/utilisateurs/me/verify-password', { password: passwordModal.password });
      if (response.data.success) {
        await executeToggle(passwordModal.roleId, passwordModal.permissionCode);
        setPasswordModal({ isOpen: false, roleId: null, permissionCode: null, password: "", error: "", isLoading: false });
      }
    } catch (err) {
      setPasswordModal(prev => ({ ...prev, isLoading: false, error: "Mot de passe incorrect." }));
    }
  };



  const handleCreateRole = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/roles', { name: newRoleName, color: newRoleColor });
      setRoles([...roles, { ...res.data, rolePermissions: [] }]);
      setSelectedRoleId(res.data.id);
      setIsRoleModalOpen(false);
      setNewRoleName("");
      showToast("Rôle créé avec succès", "success");
    } catch (err) {
      showToast("Erreur lors de la création du rôle", "error");
    }
  };

  const { confirm } = useModal();

  const handleDeleteRole = async (id) => {
    const isConfirmed = await confirm({
      title: "Supprimer le rôle",
      message: "Voulez-vous vraiment supprimer ce rôle ?",
      type: "danger",
      confirmText: "Supprimer",
      cancelText: "Annuler"
    });
    if (!isConfirmed) return;
    try {
      await api.delete(`/roles/${id}`);
      setRoles(roles.filter(r => r.id !== id));
      if (selectedRoleId === id) {
        setSelectedRoleId(roles[0]?.id || null);
      }
      showToast("Rôle supprimé", "success");
    } catch (err) {
      showToast("Erreur lors de la suppression", "error");
    }
  };



  if (loading) return <MiniLoader />;

  return (
    <div className="p-6 h-full flex flex-col gap-6">
      <div className="flex justify-between items-center bg-surface p-6 rounded-2xl border border-[var(--hp-color-border-muted)] shadow-sm shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-primary">Rôles & Permissions</h1>
          <p className="text-sm text-subtle mt-1">Gérez la hiérarchie des rôles et les accès aux modules du workspace.</p>
        </div>
        <button 
          onClick={() => setIsRoleModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--hp-info-600)] text-white font-medium rounded-xl shadow-sm hover:bg-[var(--hp-info-700)] transition"
        >
          <Plus className="w-5 h-5" />
          Nouveau Rôle
        </button>
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-12 gap-6">
        {/* Sidebar des Rôles (Drag & Drop) */}
        <div className="col-span-12 md:col-span-4 lg:col-span-3 bg-surface border border-[var(--hp-color-border-muted)] rounded-2xl shadow-sm flex flex-col overflow-hidden h-full">
          <div className="p-4 border-b border-[var(--hp-color-border-muted)] bg-surface-muted">
            <h2 className="text-xs font-bold text-tertiary uppercase tracking-wider">Hiérarchie</h2>
            <p className="text-[11px] text-tertiary mt-1 leading-tight">Glissez-déposez pour réordonner (le plus haut est prioritaire).</p>
          </div>
          
            <div className="flex-1 overflow-y-auto p-3">
              <DragDropContext onDragEnd={(result) => {
                if (!result.destination) return;
                const items = Array.from(sortedRoles);
                const [reorderedItem] = items.splice(result.source.index, 1);
                items.splice(result.destination.index, 0, reorderedItem);
                
                // On recalcule uniquement la `position` des rôles modifiables, mais pour simplifier 
                // et puisque on veut juste mettre à jour la liste triée, on met à jour roles.
                // Attention: Les rôles systèmes restent épinglés en haut par sortedRoles, 
                // mais on peut sauvegarder le nouvel ordre des positions.
                const updatedRoles = items.map((r, index) => ({
                  ...r,
                  position: items.length - index
                }));
            
                setRoles(updatedRoles);
            
                try {
                  api.put(`/roles/${reorderedItem.id}/position`, { position: updatedRoles[result.destination.index].position });
                } catch (error) {
                  console.error("Failed to update role position", error);
                }
              }}>
                <Droppable droppableId="roles-list">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-1.5">
                      {sortedRoles.map((role, index) => (
                        <Draggable key={role.id} draggableId={role.id} index={index} isDragDisabled={role.isSystemRole}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              onClick={() => setSelectedRoleId(role.id)}
                              className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all duration-200 border
                                ${selectedRoleId === role.id ? 'bg-[var(--hp-info-50)] border-[var(--hp-info-200)] shadow-sm' : 'bg-surface border-transparent hover:bg-surface-muted'}
                                ${snapshot.isDragging ? 'shadow-lg ring-2 ring-[var(--hp-info-500)] opacity-90' : ''}
                                ${role.isSystemRole ? 'opacity-90' : ''}
                              `}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-2.5 h-2.5 rounded-full`} style={{ backgroundColor: role.color || '#6366f1' }} />
                                <span className={`font-medium text-sm ${selectedRoleId === role.id ? 'text-[var(--hp-info-900)]' : 'text-primary'}`}>
                                  {role.name}
                                </span>
                              </div>
                              {role.code === 'SYSADMIN' && <Lock className="w-3.5 h-3.5 text-tertiary" />}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            
            {roles.length === 0 && (
              <div className="text-sm text-tertiary p-2 italic text-center mt-10">Aucun rôle défini.</div>
            )}
          </div>
        </div>

        {/* Colonne 2: Rubriques */}
        {selectedRole ? (
          <>
            <AnimatePresence mode="popLayout">
              {selectedRole.code !== 'SYSADMIN' && (
                <motion.div 
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  className="col-span-12 md:col-span-4 lg:col-span-3 bg-surface border border-[var(--hp-color-border-muted)] rounded-2xl shadow-sm flex flex-col h-full overflow-hidden"
                >
                  <div className="p-4 border-b border-[var(--hp-color-border-muted)] bg-surface-muted">
                    <h2 className="text-xs font-bold text-tertiary uppercase tracking-wider">Rubriques</h2>
                    <p className="text-[11px] text-tertiary mt-1 leading-tight">Sélectionnez une catégorie</p>
                  </div>
                  <div className="flex-1 overflow-y-auto p-3 space-y-1 scrollbar-thin">
                    {PERMISSIONS_SCHEMA.map(section => {
                      const IconComponent = IconMap[section.icon] || LayoutDashboard;
                      return (
                        <button
                          key={section.category}
                          onClick={() => setSelectedCategory(section.category)}
                          className={`w-full flex items-center justify-between p-3 rounded-xl transition-all duration-200 border ${selectedCategory === section.category ? 'bg-[var(--hp-info-50)] border-[var(--hp-info-200)] shadow-sm text-[var(--hp-info-900)]' : 'bg-surface border-transparent text-primary hover:bg-surface-muted'}`}
                        >
                          <div className="flex items-center gap-3">
                            <IconComponent className={`w-4 h-4 ${selectedCategory === section.category ? 'text-[var(--hp-info-600)]' : 'text-subtle'}`} />
                            <span className="font-medium text-sm">{section.category}</span>
                          </div>
                          <ChevronRight className={`w-4 h-4 ${selectedCategory === section.category ? 'text-[var(--hp-info-500)]' : 'text-transparent'}`} />
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Colonne 3: Matrice des permissions */}
            <motion.div 
              layout 
              transition={{ duration: 0.2 }}
              className={`col-span-12 ${selectedRole.code === 'SYSADMIN' ? 'md:col-span-8 lg:col-span-9' : 'md:col-span-8 lg:col-span-6'} bg-surface border border-[var(--hp-color-border-muted)] rounded-2xl shadow-sm flex flex-col h-full overflow-hidden`}
            >
              {/* Header */}
              <div className="p-6 border-b border-[var(--hp-color-border-muted)] bg-surface-muted flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <label className="cursor-pointer relative overflow-hidden rounded-xl">
                    <div className="w-10 h-10 flex items-center justify-center shadow-sm bg-white" style={{ border: `1px solid ${selectedRole.color || '#6366f1'}` }}>
                      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: selectedRole.color || '#6366f1' }} />
                    </div>
                    <input 
                      type="color" 
                      className="absolute inset-0 opacity-0 cursor-pointer w-full h-full" 
                      value={selectedRole.color || '#6366f1'} 
                      onChange={async (e) => {
                        const newColor = e.target.value;
                        setRoles(roles.map(r => r.id === selectedRole.id ? { ...r, color: newColor } : r));
                        try {
                          await api.put(`/roles/${selectedRole.id}`, { color: newColor });
                        } catch(err) {
                          console.error("Color change error", err);
                        }
                      }}
                    />
                  </label>
                  <div>
                    <h2 className="text-lg font-bold text-primary flex items-center gap-2">
                      {selectedRole.name}
                      {!selectedRole.isSystemRole && (
                        <button onClick={() => handleDeleteRole(selectedRole.id)} className="text-subtle hover:text-[var(--hp-danger-600)] transition">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </h2>
                    <p className="text-xs text-subtle mt-0.5">Configuration des droits d'accès. Sauvegarde automatique.</p>
                  </div>
                </div>
              </div>

              {/* Grid Permissions */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                <AnimatePresence mode="wait">
                  {selectedRole.code === 'SYSADMIN' ? (
                    <motion.div 
                      key="sysadmin-lock"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.15 }}
                      className="flex flex-col items-center justify-center text-center h-full text-subtle space-y-4 pt-12"
                    >
                      <div className="w-16 h-16 bg-[var(--hp-info-50)] rounded-full flex items-center justify-center mb-2">
                        <Lock className="w-8 h-8 text-[var(--hp-info-600)]" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-primary">Accès Total Inaltérable</h3>
                        <p className="mt-2 text-sm max-w-sm mx-auto">Ce rôle dispose d'un accès administrateur système complet. Ses permissions ne peuvent pas être modifiées.</p>
                      </div>
                    </motion.div>
                  ) : (
                    (() => {
                      const section = PERMISSIONS_SCHEMA.find(s => s.category === selectedCategory);
                      if (!section) return null;

                      const masterGranted = selectedRole.rolePermissions?.some(rp => rp.permission.code === section.masterCode);

                      return (
                        <motion.div 
                          key={section.category}
                          initial={{ opacity: 0, x: 10 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -10 }}
                          transition={{ duration: 0.15 }}
                        >
                      {/* En-tête Master Permission */}
                      <div className="flex items-center justify-between bg-surface-muted p-4 rounded-xl border border-[var(--hp-color-border-muted)] shadow-sm mb-6">
                        <div>
                          <h3 className="text-sm font-bold text-primary">Accès {section.category}</h3>
                          <p className="text-xs text-subtle mt-0.5">Activez pour autoriser l'accès au module.</p>
                        </div>
                        <button
                          onClick={() => togglePermission(selectedRole.id, section.masterCode, 'STANDARD')}
                          className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[var(--hp-info-500)] focus:ring-offset-2 ${masterGranted ? 'bg-[var(--hp-info-600)]' : 'bg-gray-300 dark:bg-gray-700'}`}
                        >
                          <span className="sr-only">Activer l'accès {section.category}</span>
                          <span
                            aria-hidden="true"
                            className={`pointer-events-none absolute left-0 inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition-transform duration-200 ease-in-out ${masterGranted ? 'translate-x-4' : 'translate-x-0.5'}`}
                          />
                        </button>
                      </div>

                      {section.permissions.length > 0 && (
                        <div className={`overflow-hidden rounded-xl border border-[var(--hp-color-border-muted)] shadow-sm transition-opacity ${masterGranted ? 'opacity-100' : 'opacity-50 pointer-events-none'}`}>
                          <table className="w-full text-sm text-left">
                            <thead className="bg-surface-muted border-b border-[var(--hp-color-border-muted)] text-xs uppercase text-tertiary font-bold tracking-wider">
                              <tr>
                                <th scope="col" className="px-6 py-3 w-1/2">Permission ({section.category})</th>
                                <th scope="col" className="px-6 py-3 text-center">Risque</th>
                                <th scope="col" className="px-6 py-3 text-right">Accès</th>
                              </tr>
                            </thead>
                            <motion.tbody 
                              className="divide-y divide-[var(--hp-color-border-subtle)] bg-surface"
                              initial="hidden"
                              animate="visible"
                              variants={{
                                hidden: { opacity: 0 },
                                visible: {
                                  opacity: 1,
                                  transition: { staggerChildren: 0.05 }
                                }
                              }}
                            >
                              {section.permissions.map(perm => {
                                const isGranted = selectedRole.rolePermissions?.some(rp => rp.permission.code === perm.code);
                                const isCritical = perm.riskLevel === 'CRITICAL';
                                const isSensitive = perm.riskLevel === 'SENSITIVE';

                                return (
                                  <motion.tr 
                                    key={perm.code} 
                                    className="hover:bg-[var(--hp-color-bg-hover)] transition-colors group"
                                    variants={{
                                      hidden: { opacity: 0, x: -10 },
                                      visible: { opacity: 1, x: 0 }
                                    }}
                                  >
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium text-primary">{perm.label}</span>
                                        <span className="text-[10px] text-tertiary font-mono bg-surface-muted px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                                          {perm.code}
                                        </span>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                      {isCritical ? (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-[var(--hp-danger-50)] text-[var(--hp-danger-700)] border border-[var(--hp-danger-200)]">
                                          <ShieldAlert className="w-3.5 h-3.5" /> Critique
                                        </span>
                                      ) : isSensitive ? (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-[var(--hp-warning-50)] text-[var(--hp-warning-700)] border border-[var(--hp-warning-200)]">
                                          <ShieldCheck className="w-3.5 h-3.5" /> Sensible
                                        </span>
                                      ) : (
                                        <span className="text-tertiary text-xs">—</span>
                                      )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right">
                                      <button
                                        onClick={() => togglePermission(selectedRole.id, perm.code, perm.riskLevel)}
                                        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[var(--hp-info-500)] focus:ring-offset-2 ${isGranted ? 'bg-[var(--hp-info-600)]' : 'bg-gray-300 dark:bg-gray-700'}`}
                                      >
                                        <span className="sr-only">Activer {perm.label}</span>
                                        <span
                                          aria-hidden="true"
                                          className={`pointer-events-none absolute left-0 inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition-transform duration-200 ease-in-out ${isGranted ? 'translate-x-4' : 'translate-x-0.5'}`}
                                        />
                                      </button>
                                    </td>
                                  </motion.tr>
                                );
                              })}
                            </motion.tbody>
                          </table>
                        </div>
                      )}
                        </motion.div>
                      );
                    })()
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </>
        ) : (
          <div className="col-span-12 md:col-span-8 lg:col-span-9 bg-surface border border-[var(--hp-color-border-muted)] rounded-2xl shadow-sm flex flex-col h-full overflow-hidden items-center justify-center text-subtle space-y-4">
            <ShieldCheck className="w-16 h-16 opacity-50" />
            <p>Sélectionnez un rôle pour configurer ses permissions.</p>
          </div>
        )}
      </div>

      {/* Modal Création Rôle */}
      <AnimatePresence>
        {isRoleModalOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
             <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-ink-900/60 backdrop-blur-sm" onClick={() => setIsRoleModalOpen(false)} />
             <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 20 }} className="relative bg-surface rounded-xl shadow-hp-2xl w-full max-w-md overflow-hidden border border-[var(--hp-color-border-muted)]">
                <div className="flex justify-between items-center p-6 border-b border-[var(--hp-color-border-muted)]">
                  <h3 className="text-xl font-bold text-primary">Nouveau Rôle</h3>
                  <button onClick={() => setIsRoleModalOpen(false)} className="text-subtle hover:text-primary transition p-1"><X className="w-5 h-5" /></button>
                </div>
                <form onSubmit={handleCreateRole} className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-primary mb-1">Nom du rôle</label>
                    <input type="text" required className="workspace-input w-full" value={newRoleName} onChange={(e) => setNewRoleName(e.target.value)} placeholder="Ex: Stagiaire, Auditeur..." />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary mb-1">Couleur</label>
                    <input type="color" className="w-full h-10 rounded-lg cursor-pointer border border-[var(--hp-color-border-muted)]" value={newRoleColor} onChange={(e) => setNewRoleColor(e.target.value)} />
                  </div>
                  <div className="pt-4 flex justify-end gap-3 border-t border-[var(--hp-color-border-subtle)] mt-4">
                    <button type="button" onClick={() => setIsRoleModalOpen(false)} className="btn btn-secondary">Annuler</button>
                    <button type="submit" className="btn btn-primary bg-[var(--hp-info-600)] hover:bg-[var(--hp-info-700)] text-white border-transparent">Créer le rôle</button>
                  </div>
                </form>
             </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal for Password Verification */}
      <AnimatePresence>
        {passwordModal.isOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-ink-900/60 backdrop-blur-sm" />
            <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 20 }} className="relative bg-surface rounded-2xl shadow-xl w-full max-w-md overflow-hidden border border-[var(--hp-color-border-muted)]">
              <form onSubmit={handlePasswordConfirm}>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4 text-[var(--hp-danger-600)]">
                    <ShieldAlert className="w-8 h-8" />
                    <h3 className="text-xl font-bold">Autorisation Critique</h3>
                  </div>
                  <p className="text-sm text-primary mb-4">
                    La modification des accès de type <span className="font-mono bg-[var(--hp-danger-50)] text-[var(--hp-danger-700)] px-1 py-0.5 rounded font-semibold">Administrateur</span> nécessite la vérification de votre mot de passe.
                  </p>
                  
                  {passwordModal.error && (
                    <div className="mb-4 p-3 rounded-lg bg-[var(--hp-danger-50)] text-[var(--hp-danger-700)] text-sm border border-[var(--hp-danger-200)] flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      {passwordModal.error}
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-primary">Mot de passe</label>
                    <input 
                      type="password"
                      className="workspace-input w-full"
                      placeholder="Votre mot de passe..."
                      value={passwordModal.password}
                      onChange={(e) => setPasswordModal({ ...passwordModal, password: e.target.value, error: "" })}
                      required
                      autoFocus
                    />
                  </div>
                </div>
                <div className="bg-surface-muted px-6 py-4 border-t border-[var(--hp-color-border-subtle)] flex justify-end gap-3">
                  <button type="button" onClick={() => setPasswordModal({ isOpen: false, roleId: null, permissionCode: null, password: "", error: "", isLoading: false })} className="btn btn-secondary">
                    Annuler
                  </button>
                  <button type="submit" disabled={!passwordModal.password || passwordModal.isLoading} className="btn btn-primary bg-[var(--hp-danger-600)] hover:bg-[var(--hp-danger-700)] text-white border-transparent disabled:opacity-50">
                    {passwordModal.isLoading ? "Vérification..." : "Confirmer"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RolesManager;
